import React from 'react';
import { StyleTokens, useTemplateStore } from './templateStore';

export const TemplateStylePanel: React.FC<{ templateId: string }>
  = ({ templateId }) => {
  const { templates, updateTemplate } = useTemplateStore();
  const t = templates.find(x => x.id === templateId);
  if (!t) return null;
  const s: StyleTokens = t.styleTokens || {};

  function patch(p: Partial<StyleTokens>) {
    // normalize nested objects to satisfy strict types
    const next: StyleTokens = {
      fontFamily: p.fontFamily ?? s.fontFamily,
      fontSize: p.fontSize ?? s.fontSize,
      margins: {
        top: p.margins?.top ?? s.margins?.top ?? 1,
        right: p.margins?.right ?? s.margins?.right ?? 1,
        bottom: p.margins?.bottom ?? s.margins?.bottom ?? 1,
        left: p.margins?.left ?? s.margins?.left ?? 1,
      },
      pleadingPaper: {
        enabled: p.pleadingPaper?.enabled ?? s.pleadingPaper?.enabled ?? false,
        lineNumberStart: p.pleadingPaper?.lineNumberStart ?? s.pleadingPaper?.lineNumberStart ?? 1,
        lineNumberEnd: p.pleadingPaper?.lineNumberEnd ?? s.pleadingPaper?.lineNumberEnd ?? 28,
        gutter: p.pleadingPaper?.gutter ?? s.pleadingPaper?.gutter ?? 0.5,
      },
    };
    updateTemplate(templateId, { styleTokens: next });
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Style</h4>
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-600 dark:text-gray-400">Apply a jurisdiction preset</div>
        <button
          title="Sets 12pt font, 1-inch margins, 0.5in gutter, and 28 lines"
          className="px-2 py-1 border rounded text-xs"
          onClick={() => {
            const preset: StyleTokens = {
              fontFamily: 'Times New Roman',
              fontSize: 12,
              margins: { top: 1, right: 1, bottom: 1, left: 1 },
              pleadingPaper: { enabled: true, lineNumberStart: 1, lineNumberEnd: 28, gutter: 0.5 },
            };
            updateTemplate(templateId, { styleTokens: preset });
          }}
        >
          Apply CA Standard
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <label className="flex items-center gap-2">
          <span className="w-28 text-gray-700 dark:text-gray-300">Font</span>
          <input title="Typeface used across the template" className="border rounded px-2 py-1 flex-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.fontFamily || ''} onChange={e => patch({ fontFamily: e.target.value })} />
        </label>
        <label className="flex items-center gap-2">
          <span className="w-28 text-gray-700 dark:text-gray-300">Size (pt)</span>
          <input title="Base font size in points (12pt recommended)" type="number" className="border rounded px-2 py-1 w-24 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.fontSize || 12} onChange={e => patch({ fontSize: Number(e.target.value) })} />
        </label>
        <div className="col-span-2 grid grid-cols-4 gap-2">
          <label className="flex items-center gap-2"><span className="text-gray-700 dark:text-gray-300">Top</span>
            <input title="Top margin in inches" type="number" step="0.1" className="border rounded px-2 py-1 w-20 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.margins?.top ?? 1} onChange={e => patch({ margins: { top: Number(e.target.value) } as any })} />
          </label>
          <label className="flex items-center gap-2"><span className="text-gray-700 dark:text-gray-300">Right</span>
            <input title="Right margin in inches" type="number" step="0.1" className="border rounded px-2 py-1 w-20 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.margins?.right ?? 1} onChange={e => patch({ margins: { right: Number(e.target.value) } as any })} />
          </label>
          <label className="flex items-center gap-2"><span className="text-gray-700 dark:text-gray-300">Bottom</span>
            <input title="Bottom margin in inches" type="number" step="0.1" className="border rounded px-2 py-1 w-20 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.margins?.bottom ?? 1} onChange={e => patch({ margins: { bottom: Number(e.target.value) } as any })} />
          </label>
          <label className="flex items-center gap-2"><span className="text-gray-700 dark:text-gray-300">Left</span>
            <input title="Left margin in inches" type="number" step="0.1" className="border rounded px-2 py-1 w-20 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.margins?.left ?? 1} onChange={e => patch({ margins: { left: Number(e.target.value) } as any })} />
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300" title="Enable California-style pleading line numbers and gutter">
          <input type="checkbox" checked={!!s.pleadingPaper?.enabled} onChange={e => patch({ pleadingPaper: { enabled: e.target.checked } })} />
          Pleading Paper (line numbering)
        </label>
        {s.pleadingPaper?.enabled && (
          <div className="grid grid-cols-3 gap-2 text-sm">
            <label className="flex items-center gap-2" title="First line number shown on each page"><span className="text-gray-700 dark:text-gray-300">Start</span>
              <input type="number" className="border rounded px-2 py-1 w-20 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.pleadingPaper?.lineNumberStart ?? 1} onChange={e => patch({ pleadingPaper: { enabled: !!s.pleadingPaper?.enabled, lineNumberStart: Number(e.target.value) } })} />
            </label>
            <label className="flex items-center gap-2" title="Last line number (28 is standard in CA)"><span className="text-gray-700 dark:text-gray-300">End</span>
              <input type="number" className="border rounded px-2 py-1 w-20 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.pleadingPaper?.lineNumberEnd ?? 28} onChange={e => patch({ pleadingPaper: { enabled: !!s.pleadingPaper?.enabled, lineNumberEnd: Number(e.target.value) } })} />
            </label>
            <label className="flex items-center gap-2" title="Space reserved for line numbers (inches)"><span className="text-gray-700 dark:text-gray-300">Gutter (in)</span>
              <input type="number" step="0.1" className="border rounded px-2 py-1 w-24 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={s.pleadingPaper?.gutter ?? 0.5} onChange={e => patch({ pleadingPaper: { enabled: !!s.pleadingPaper?.enabled, gutter: Number(e.target.value) } })} />
            </label>
          </div>
        )}
        {s.pleadingPaper?.enabled && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <label className="flex items-center gap-2" title="Show a light gray gutter background at the left">
              <input
                type="checkbox"
                defaultChecked
                onChange={(e) => {
                  const el = document.querySelector('.pleading-paper') as HTMLElement | null;
                  if (!el) return;
                  el.style.setProperty('--pp-gutter-shade', e.target.checked ? 'linear-gradient(to right, rgba(0,0,0,0.035) var(--pp-margin-left), transparent var(--pp-margin-left))' : 'transparent');
                }}
              />
              Gutter shading
            </label>
            <label className="flex items-center gap-2" title="Show a vertical rule between gutter and content">
              <input
                type="checkbox"
                defaultChecked
                onChange={(e) => {
                  const el = document.querySelector('.pleading-paper') as HTMLElement | null;
                  if (!el) return;
                  el.style.setProperty('--pp-vertical-rule', e.target.checked ? '1px solid rgba(0,0,0,0.25)' : 'none');
                }}
              />
              Vertical rule
            </label>
          </div>
        )}
      </div>
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
        <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Caption</h5>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!(t as any).captionStyle?.titleBold}
            onChange={(e) => updateTemplate(templateId, { captionStyle: { ...(t as any).captionStyle, titleBold: e.target.checked } } as any)}
          />
          Title bold by default
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!(t as any).captionStyle?.titleItalic}
            onChange={(e) => updateTemplate(templateId, { captionStyle: { ...(t as any).captionStyle, titleItalic: e.target.checked } } as any)}
          />
          Title italic by default
        </label>
      </div>
    </div>
  );
};

export default TemplateStylePanel;


